import React, { useState, useRef, useEffect } from "react";
import { View, StyleSheet, ScrollView, Platform } from "react-native";
import {
  KeyboardAvoidingView,
  KeyboardStickyView,
} from "react-native-keyboard-controller";
import Metrics from "@constants/metrics";
import InputBox from "@components/InputBox";
import Button from "@components/Button";
import { useDispatch, useSelector } from "react-redux";
import AppText from "@components/AppText";
import colors from "@constants/color";
import { numberValidator, lengthValidator } from "@utils/validators";
import { actions } from "@reducers/authReducer";
import { manageBankDetails } from "@services/api";
import { actions as configActions } from "@reducers/configReducer";
import analytics from "@services/analytics";
import t from "@constants/localization";

import {
  ADD_BANK_DETAILS,
  EDIT_BANK_DETAILS,
} from "../../constants/analyticsEvents";

const PATH = "screens/BankDetailsForm";

const validationJSON = {
  account_number: {
    min: 8,
    max: 25,
    isNumber: true,
  },
  ifsc_code: {
    min: 11,
    max: 11,
  },
  bank_name: {
    min: 2,
    max: 50,
  },
  account_holder_name: {
    min: 4,
    max: 50,
  },
};

const BankDetailsForm = (props) => {
  const [state, setState] = useState({
    bank: {
      account_number: "",
      ifsc_code: "",
      bank_name: "",
      account_holder_name: "",
    },
    displayErrors: {},
    error: false,
    mode: "add",
    isLoading: false,
  });
  const inputRefs = useRef({
    account_holder_name: null,
    account_number: null,
    ifsc_code: null,
    bank_name: null,
  });

  const { user: { id = "" } = {} } = useSelector((state) => state.auth);

  const dispatch = useDispatch();

  const setFormValues = (key = "", value = "") => {
    setState((state) => ({
      ...state,
      bank: {
        ...state.bank,
        [key]: value,
      },
    }));
  };

  useEffect(() => {
    if (
      props.route &&
      props.route.params &&
      props.route.params.mode === "edit"
    ) {
      dispatch(configActions.setHeaderTitle(t(`${PATH}/updateBankAcc`)));
      setState((state) => ({
        ...state,
        mode: "edit",
        bank: props.route.params.bank_details,
      }));
    } else {
      dispatch(configActions.setHeaderTitle(t(`${PATH}/addBankAcc`)));
    }
  }, []);

  const setRefs = (name, ref) => {
    inputRefs.current[name] = ref;
  };

  const setItemToFocus = (name) => {
    const allRefs = Object.entries(inputRefs.current);

    const currentItemIndex = allRefs.findIndex((item) => item[0] === name);
    let item = null;

    if (currentItemIndex !== -1 && currentItemIndex !== allRefs.length - 1) {
      item = allRefs[currentItemIndex + 1][1];
    }

    if (item) {
      item.focus();
    }
  };

  const formValidator = () => {
    let isValid = true;
    const errorObj = {};
    const valueObj = { ...state.bank };

    Object.keys(valueObj).forEach((v) => {
      if (validationJSON[v]) {
        if (
          validationJSON[v].isNumber &&
          !numberValidator(
            valueObj[v],
            validationJSON[v].min,
            validationJSON[v].max,
          )
        ) {
          isValid = isValid ? false : isValid;
          errorObj[v] = true;
        } else if (
          !lengthValidator(
            valueObj[v],
            validationJSON[v].min,
            validationJSON[v].max,
          )
        ) {
          isValid = isValid ? false : isValid;
          errorObj[v] = true;
        }
      }
    });

    return { isValid, errorObj };
  };

  const handleSubmit = async () => {
    if (!state.displayErrors.isValid) {
      setState((state) => ({
        ...state,
        displayErrors: {},
      }));
    }
    const errorData = formValidator();

    if (!errorData.isValid) {
      setState((state) => ({
        ...state,
        displayErrors: errorData.errorObj || {},
      }));
    } else {
      try {
        const userData = { ...state.bank };
        const { data } = await manageBankDetails(userData);

        if (mode === "edit") {
          // analytics.trackEvent(EDIT_BANK_DETAILS, {
          //   bankDetails: userData,
          //   leader_id: id,
          // });
        } else {
          // analytics.trackEvent(ADD_BANK_DETAILS, {
          //   bankDetails: userData,
          //   leader_id: id,
          // });
        }

        if (data.ifsc_code) {
          dispatch(actions.getUserDetailsByTokenRequest());
          props.navigation.goBack();
        }
      } catch (e) {
        console.log(e);
      }
    }
  };

  const { displayErrors, bank, error, mode } = state;

  const { account_number, ifsc_code, account_holder_name, bank_name } = bank;

  return (
    <>
      <KeyboardAvoidingView
        style={{ flex: 1, backgroundColor: colors.white }}
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        enabled
      >
        <ScrollView
          bounces={false}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{
            paddingBottom: 100,
          }}
        >
          <View style={styles.fieldsContainer}>
            <View
              style={{
                flexDirection: "row",
                width: "100%",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <View style={{ flexGrow: 1 }}>
                <InputBox
                  placeholder={t(`${PATH}/accHolderName`)}
                  value={account_holder_name}
                  labelText={t(`${PATH}/accHolderName`)}
                  required
                  onChange={(text) =>
                    setFormValues("account_holder_name", text)
                  }
                  error={displayErrors.account_holder_name}
                  errorMessage={
                    displayErrors.account_holder_name &&
                    t(`${PATH}/pleaseEnterAccHolderName`)
                  }
                  onSubmitEditing={() => setItemToFocus("account_holder_name")}
                  setRef={(view) => setRefs("account_holder_name", view)}
                />
              </View>
            </View>
            <View style={{ marginTop: Metrics.margin7 + 1 }}>
              <InputBox
                placeholder={t(`${PATH}/accNumber`)}
                value={account_number}
                labelText={t(`${PATH}/accNumber`)}
                keyboardType="phone-pad"
                required
                onChange={(number) => setFormValues("account_number", number)}
                error={displayErrors.account_number}
                errorMessage={
                  displayErrors.account_number
                    ? t(`${PATH}/accNumberRequired`)
                    : ""
                }
                onSubmitEditing={() => setItemToFocus("account_number")}
                setRef={(view) => setRefs("account_number", view)}
              />
            </View>

            <View
              style={{
                flexDirection: "row",
                width: "100%",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <View style={{ flexGrow: 1 }}>
                <InputBox
                  placeholder={t(`${PATH}/IFSCCode`)}
                  value={ifsc_code}
                  labelText={t(`${PATH}/IFSCCode`)}
                  maxLength={11}
                  required
                  onChange={(text) => setFormValues("ifsc_code", text)}
                  error={displayErrors.ifsc_code}
                  errorMessage={
                    displayErrors.ifsc_code && t(`${PATH}/IFSCCodeRequired`)
                  }
                  onSubmitEditing={() => setItemToFocus("ifsc_code")}
                  setRef={(view) => setRefs("ifsc_code", view)}
                />
              </View>
            </View>

            <View
              style={{
                flexDirection: "row",
                width: "100%",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <View style={{ flexGrow: 1, marginBottom: 20 }}>
                <InputBox
                  placeholder={t(`${PATH}/bankName`)}
                  value={bank_name}
                  labelText={t(`${PATH}/bankName`)}
                  required
                  onChange={(text) => setFormValues("bank_name", text)}
                  error={displayErrors.bank_name}
                  errorMessage={
                    displayErrors.bank_name && t(`${PATH}/bankNameRequired`)
                  }
                  onSubmitEditing={() => setItemToFocus("bank_name")}
                  setRef={(view) => setRefs("bank_name", view)}
                />
              </View>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
      <KeyboardStickyView style={styles.submitBtn}>
        <View
          style={{
            backgroundColor: "#fff",
            elevation: 5,
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            width: "100%",
            padding: Metrics.margin2,
          }}
        >
          <Button
            btnText={
              mode === "add"
                ? t(`${PATH}/addBankAcc`)
                : t(`${PATH}/updateBankAcc`)
            }
            buttonWidth="100%"
            onPress={handleSubmit}
            borderRadius={24}
            buttonFontType="content1"
            // disabled={this.shouldDisableSubmitBtn}
          />
        </View>
      </KeyboardStickyView>
    </>
  );
};

const styles = StyleSheet.create({
  submitBtn: {
    position: "absolute",
    width: "100%",
    bottom: 0,
  },
  fieldsContainer: {
    padding: 14,
  },
  topText: {
    fontSize: 16,
    lineHeight: 24,
  },
});

export default BankDetailsForm;
